import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../utils/userRoles';
import Switch from '../formElements/Switch';
import AccordionHeader from '../ui/accordion/AccordionHeader';
import AccordionIcon from '../ui/accordion/AccordionIcon';
import AccordionSubHeader from '../ui/accordion/AccordionSubHeader';

import ActionPlansAdmin from './actionPlanAdmin/ActionPlansAdmin';
import AppointmentsAdmin from './appointmentsAdmin/AppointmentsAdmin';
import AssetRequestsAdmin from './assetRequestsAdmin/AssetRequestsAdmin';
import BarriersAdmin from './barriersAdmin/BarriersAdmin';
import CircumstancesAdmin from './circumstancesAdmin/CircumstancesAdmin';
import CommissionerCommunicationsAdmin from './commissionerCommunicationsAdmin/CommissionerCommunicationsAdmin';
import ContractDetailsAdmin from './contractDetailsAdmin/ContractDetailsAdmin';
import CoursesAdmin from './coursesAdmin/CoursesAdmin';
import DisclaimerAdmin from './disclaimerAdmin/DisclaimerAdmin';
import DocumentsAdmin from './documentsAdmin/DocumentsAdmin';
import FinancialRequestsAdmin from './financialRequestsAdmin/FinancialRequestsAdmin';
import FormsDefaultsConfigAdmin from './formsDefaultsConfigAdmin/FormsDefaultsConfigAdmin';
import HealthInformationAdmin from './healthInformationAdmin/HealthInformationAdmin';
import ParticipantInformationAdmin from './participantInformationAdmin/ParticipantInformationAdmin';
import ParticipantPrivacyAdmin from './participantPrivacyAdmin/ParticipantPrivacyAdmin';
import ParticipantQuestionnairesAndFormsAdmin from './participantQuestionnairesAndFormsAdmin/ParticipantQuestionnairesAndFormsAdmin';
import ParticipantStatusAdmin from './participantStatusAdmin/ParticipantStatusAdmin';
import PrimaryDetailsAdmin from './primaryDetailsAdmin/PrimaryDetailsAdmin';
import TimelineAdmin from './timelineAdmin/TimelineAdmin';
import WellbeingAdmin from './wellbeingAdmin/WellbeingAdmin';
import EmployabilityAdmin from './EmployabilityAdmin';
import PlacementsAdmin from './PlacementsAdmin';

import app from '../../app.module.css';
import local from './adminStyles.module.css';

const AdminManagement = ({ isHeading, contractId, onHeadingChange, switchMessages }) => {
    // LOCAL STATE
    const [expanded, setExpanded] = useState('');
    const [expandedInnerPanel, setExpandedInnerPanel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);

    // USEEFFECTS
    useEffect(() => {
        setExpanded('');
        setExpandedInnerPanel('');
    }, [contractId]);

    // FNs
    const onToggleHeading = (key, e) => {
        e.stopPropagation();
        onHeadingChange((prev) => ({ ...prev, [key]: e.target.checked }));
    };

    const onPanelChange = (panel) => {
        setExpanded(expanded === panel ? '' : panel);
        setExpandedInnerPanel('');
    };

    const onInnerPanelChange = (panel) =>
        setExpandedInnerPanel(expandedInnerPanel === panel ? '' : panel);

    // RENDER
    return (
        <div className={app.pageContainer}>
            <div>
                <Accordion
                    expanded={expanded === 'contractDetails'}
                    onChange={() => onPanelChange('contractDetails')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">
                        <AccordionHeader>Contract Details</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'contractDetails' && (
                            <ContractDetailsAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'primaryDetails'}
                    onChange={() => onPanelChange('primaryDetails')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel1b-content"
                        id="panel1b-header">
                        <AccordionHeader>Primary Details</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'primaryDetails' && (
                            <PrimaryDetailsAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'participantInformation'}
                    onChange={() => onPanelChange('participantInformation')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Participant Information</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'participantInformation' && (
                            <ParticipantInformationAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>

            <Accordion
                expanded={expanded === 'personalInformation'}
                onChange={() => onPanelChange('personalInformation')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="panel3a-content"
                    id="panel3a-header">
                    <AccordionHeader>Personal Information</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'personalInformation' && (
                        <>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'healthInformation'}
                                    onChange={() => onInnerPanelChange('healthInformation')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3b-content"
                                        id="panel3b-header">
                                        <AccordionSubHeader>Health Information</AccordionSubHeader>
                                        <Switch
                                            id="isHealthInformation"
                                            checked={isHeading.healthInformation}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) =>
                                                onToggleHeading('healthInformation', e)
                                            }
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expandedInnerPanel === 'healthInformation' && (
                                            <HealthInformationAdmin contractId={contractId} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'circumstances'}
                                    onChange={() => onInnerPanelChange('circumstances')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3c-content"
                                        id="panel3c-header">
                                        <AccordionSubHeader>Circumstances</AccordionSubHeader>
                                        <Switch
                                            id="isCircumstances"
                                            checked={isHeading.circumstances}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('circumstances', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expandedInnerPanel === 'circumstances' && (
                                            <CircumstancesAdmin contractId={contractId} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'employability'}
                                    onChange={() => onInnerPanelChange('employability')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3d-content"
                                        id="panel3d-header">
                                        <AccordionSubHeader>Employability</AccordionSubHeader>
                                        <Switch
                                            id="isEmployability"
                                            checked={isHeading.employability}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('employability', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expandedInnerPanel === 'employability' && (
                                            <EmployabilityAdmin contractId={contractId} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'environment'}
                                    onChange={() => onInnerPanelChange('environment')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3e-content"
                                        id="panel3e-header">
                                        <AccordionSubHeader>Environment</AccordionSubHeader>
                                        <Switch
                                            id="isEnvironment"
                                            checked={isHeading.environment}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('environment', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'connectToWork'}
                                    onChange={() => onInnerPanelChange('connectToWork')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3f-content"
                                        id="panel3f-header">
                                        <AccordionSubHeader>Connect to Work</AccordionSubHeader>
                                        <Switch
                                            id="isConnectToWork"
                                            checked={isHeading.connectToWork}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('connectToWork', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'ltcp'}
                                    onChange={() => onInnerPanelChange('ltcp')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3g-content"
                                        id="panel3g-header">
                                        <AccordionSubHeader>LTCP</AccordionSubHeader>
                                        <Switch
                                            id="isLtcp"
                                            checked={isHeading.ltcp}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('ltcp', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 't2dr'}
                                    onChange={() => onInnerPanelChange('t2dr')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3h-content"
                                        id="panel3h-header">
                                        <AccordionSubHeader>T2DR</AccordionSubHeader>
                                        <Switch
                                            id="isT2dr"
                                            checked={isHeading.t2dr}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('t2dr', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'ndppData'}
                                    onChange={() => onInnerPanelChange('ndppData')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3i-content"
                                        id="panel3i-header">
                                        <AccordionSubHeader>NDPP Data (Open)</AccordionSubHeader>
                                        <Switch
                                            id="isNdppData"
                                            checked={isHeading.ndppData}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('ndppData', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'bsop'}
                                    onChange={() => onInnerPanelChange('bsop')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel3j-content"
                                        id="panel3j-header">
                                        <AccordionSubHeader>BSOP</AccordionSubHeader>
                                        <Switch
                                            id="isBsopData"
                                            checked={isHeading.bsop}
                                            onOff={true}
                                            inline={true}
                                            onChange={(e) => onToggleHeading('bsop', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails></AccordionDetails>
                                </Accordion>
                            </div>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>

            <div>
                <Accordion
                    expanded={expanded === 'disclaimer'}
                    onChange={() => onPanelChange('disclaimer')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel21b-content"
                        id="panel21b-header">
                        <AccordionHeader>Disclaimer</AccordionHeader>
                        <Switch
                            id="isDisclaimer"
                            checked={isHeading.disclaimer}
                            disabled={switchMessages.disclaimer}
                            onOff={true}
                            inline={true}
                            message={switchMessages.disclaimer}
                            onChange={(e) => onToggleHeading('disclaimer', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'disclaimer' && <DisclaimerAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>

            <div>
                <Accordion
                    expanded={expanded === 'participantPrivacy'}
                    onChange={() => onPanelChange('participantPrivacy')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel21a-content"
                        id="panel21a-header">
                        <AccordionHeader>Participant Privacy</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'participantPrivacy' && (
                            <ParticipantPrivacyAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>

            <div>
                <Accordion
                    expanded={expanded === 'participantBarriers'}
                    onChange={() => onPanelChange('participantBarriers')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel4a-content"
                        id="panel4a-header">
                        <AccordionHeader>Participant Barriers</AccordionHeader>
                        <Switch
                            id="isParticipantBarriers"
                            checked={isHeading.participantBarriers}
                            disabled={switchMessages.participantBarriers}
                            onOff={true}
                            inline={true}
                            message={switchMessages.participantBarriers}
                            onChange={(e) => onToggleHeading('participantBarriers', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'participantBarriers' && (
                            <BarriersAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'actionPlans'}
                    onChange={() => onPanelChange('actionPlans')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER, LOCAL_ADMIN], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel5a-content"
                        id="panel5a-header">
                        <AccordionHeader>Action Plan and Goals</AccordionHeader>
                        <Switch
                            id="isActionPlanGoals"
                            checked={isHeading.actionPlanGoals}
                            disabled={switchMessages.actionPlanGoals}
                            onOff={true}
                            inline={true}
                            message={switchMessages.actionPlanGoals}
                            onChange={(e) => onToggleHeading('actionPlanGoals', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'actionPlans' && <ActionPlansAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'serviceStandards'}
                    onChange={() => onPanelChange('serviceStandards')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel6a-content"
                        id="panel6a-header">
                        <div className={local.dormantAccordion}>Service Standards</div>
                        <Switch
                            id="isServiceStandards"
                            checked={isHeading.serviceStandards}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('serviceStandards', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'serviceStandards' && (
                            <p>
                                Panel will be populated with information from PPL-78: Administration
                                - Service Standards .
                            </p>
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'wellbeing'}
                    onChange={() => onPanelChange('wellbeing')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel7a-content"
                        id="panel7a-header">
                        <AccordionHeader>Wellbeing</AccordionHeader>
                        <Switch
                            id="isWellbeing"
                            checked={isHeading.wellbeing}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('wellbeing', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'wellbeing' && <WellbeingAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'workRequirements'}
                    onChange={() => onPanelChange('workRequirements')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel8a-content"
                        id="panel8a-header">
                        <AccordionHeader>Work Requirements</AccordionHeader>
                        <Switch
                            id="isWorkRequirements"
                            checked={isHeading.workRequirements}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('workRequirements', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails></AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'courses'}
                    onChange={() => onPanelChange('courses')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel8b-content"
                        id="panel8b-header">
                        <AccordionHeader>Courses</AccordionHeader>
                        <Switch
                            id="isCourses"
                            checked={isHeading.courses}
                            disabled={switchMessages.courses}
                            onOff={true}
                            inline={true}
                            message={switchMessages.courses}
                            onChange={(e) => onToggleHeading('courses', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'courses' && <CoursesAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'supportedJobSearch'}
                    onChange={() => onPanelChange('supportedJobSearch')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel8b-content"
                        id="panel8b-header">
                        <AccordionHeader>Supported Job Search</AccordionHeader>
                        <Switch
                            id="isSupportedJobSearch"
                            checked={isHeading.supportedJobSearch}
                            disabled={switchMessages.supportedJobSearch}
                            onOff={true}
                            inline={true}
                            message={switchMessages.supportedJobSearch}
                            onChange={(e) => onToggleHeading('supportedJobSearch', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails></AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'workHistory'}
                    onChange={() => onPanelChange('workHistory')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel9a-content"
                        id="panel9a-header">
                        <AccordionHeader>Work History</AccordionHeader>
                        <Switch
                            id="isWorkHistory"
                            checked={isHeading.workHistory}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('workHistory', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails></AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'education'}
                    onChange={() => onPanelChange('education')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel10a-content"
                        id="panel10a-header">
                        <AccordionHeader>Education</AccordionHeader>
                        <Switch
                            id="isEducation"
                            checked={isHeading.education}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('education', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails></AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'questionnairesAndForms'}
                    onChange={() => onPanelChange('questionnairesAndForms')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER, LOCAL_ADMIN], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel15a-content"
                        id="panel15a-header">
                        <AccordionHeader>Questionnaires and Forms</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'questionnairesAndForms' && (
                            <ParticipantQuestionnairesAndFormsAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>

            <Accordion
                expanded={expanded === 'submissions'}
                onChange={() => onPanelChange('submissions')}
                slotProps={{ transition: { mountOnEnter: true } }}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="panel11a-content"
                    id="panel11a-header">
                    <AccordionHeader>Submissions</AccordionHeader>
                    <Switch
                        id="isSubmissions"
                        checked={isHeading.submissions}
                        onOff={true}
                        inline={true}
                        onChange={(e) => onToggleHeading('submissions', e)}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'submissions' && (
                        <Accordion
                            expanded={expandedInnerPanel === 'placements'}
                            onChange={() => onInnerPanelChange('placements')}
                            slotProps={{ transition: { mountOnEnter: true } }}
                            className={app.accordionDivider}>
                            <AccordionSummary
                                expandIcon={<AccordionIcon />}
                                aria-controls="panel3b-content"
                                id="panel3b-header">
                                <AccordionSubHeader>Placements</AccordionSubHeader>
                                <Switch
                                    id="isPlacements"
                                    checked={isHeading.placements}
                                    onOff={true}
                                    inline={true}
                                    onChange={(e) => onToggleHeading('placements', e)}
                                />
                            </AccordionSummary>
                            <AccordionDetails>
                                {expandedInnerPanel === 'placements' && (
                                    <PlacementsAdmin contractId={contractId} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}
                </AccordionDetails>
            </Accordion>

            <div>
                <Accordion
                    expanded={expanded === 'appointments'}
                    onChange={() => onPanelChange('appointments')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER, LOCAL_ADMIN], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel27a-content"
                        id="panel27a-header">
                        <AccordionHeader>Appointments</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'appointments' && (
                            <AppointmentsAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'documents'}
                    onChange={() => onPanelChange('documents')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER, LOCAL_ADMIN], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel31a-content"
                        id="panel31a-header">
                        <AccordionHeader>Documents</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'documents' && <DocumentsAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>

            <Accordion
                expanded={expanded === 'requests'}
                onChange={() => onPanelChange('requests')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="panel30a-content"
                    id="panel30a-header">
                    <AccordionHeader>Requests</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'requests' && (
                        <>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'financialRequests'}
                                    onChange={() => onInnerPanelChange('financialRequests')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel30b-content"
                                        id="panel30b-header">
                                        <AccordionSubHeader>Financial Requests</AccordionSubHeader>
                                        <Switch
                                            id="isFinancialRequests"
                                            checked={isHeading.financialRequests}
                                            disabled={switchMessages.financialRequests}
                                            onOff={true}
                                            inline={true}
                                            message={switchMessages.financialRequests}
                                            onChange={(e) =>
                                                onToggleHeading('financialRequests', e)
                                            }
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expandedInnerPanel === 'financialRequests' && (
                                            <FinancialRequestsAdmin contractId={contractId} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                            <div>
                                <Accordion
                                    expanded={expandedInnerPanel === 'assetRequests'}
                                    onChange={() => onInnerPanelChange('assetRequests')}
                                    slotProps={{ transition: { mountOnEnter: true } }}
                                    className={app.accordionDivider}>
                                    <AccordionSummary
                                        expandIcon={<AccordionIcon />}
                                        aria-controls="panel30c-content"
                                        id="panel30c-header">
                                        <AccordionSubHeader>Asset Requests</AccordionSubHeader>
                                        <Switch
                                            id="isAssetRequests"
                                            checked={isHeading.assetRequests}
                                            disabled={switchMessages.assetRequests}
                                            onOff={true}
                                            inline={true}
                                            message={switchMessages.assetRequests}
                                            onChange={(e) => onToggleHeading('assetRequests', e)}
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expandedInnerPanel === 'assetRequests' && (
                                            <AssetRequestsAdmin contractId={contractId} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>

            <div>
                <Accordion
                    expanded={expanded === 'participantStatus'}
                    onChange={() => onPanelChange('participantStatus')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel32a-content"
                        id="panel32a-header">
                        <AccordionHeader>Participant Status</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'participantStatus' && (
                            <ParticipantStatusAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'formsDefaultsConfig'}
                    onChange={() => onPanelChange('formsDefaultsConfig')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel33a-content"
                        id="panel33a-header">
                        <AccordionHeader>Forms, Defaults & Configuration</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'formsDefaultsConfig' && (
                            <FormsDefaultsConfigAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'commissionerCommunications'}
                    onChange={() => onPanelChange('commissionerCommunications')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel34a-content"
                        id="panel34a-header">
                        <AccordionHeader>Commissioner Communications</AccordionHeader>
                        <Switch
                            id="isCommissionerCommunications"
                            checked={isHeading.commissionerCommunications}
                            onOff={true}
                            inline={true}
                            onChange={(e) => onToggleHeading('commissionerCommunications', e)}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'commissionerCommunications' && (
                            <CommissionerCommunicationsAdmin contractId={contractId} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </div>
            <div>
                <Accordion
                    expanded={expanded === 'timeline'}
                    onChange={() => onPanelChange('timeline')}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    hidden={!hasRole([SUPERUSER], roles)}
                    className={app.accordionDivider}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel35a-content"
                        id="panel35a-header">
                        <AccordionHeader>Timeline</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'timeline' && <TimelineAdmin contractId={contractId} />}
                    </AccordionDetails>
                </Accordion>
            </div>
        </div>
    );
};

AdminManagement.propTypes = {
    isHeading: PropTypes.object.isRequired,
    contractId: PropTypes.string.isRequired,
    onHeadingChange: PropTypes.func.isRequired,
    switchMessages: PropTypes.object
};

export default AdminManagement;
